import Accordion from '@ui/Accordion';
import Checkbox from '@ui/Checkbox';
import { slugify } from '@util/urlHelpers';
import { FacetCountValue } from 'models/product';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { FilterItem } from '../Filters';
import { isMobile } from '@util/index';

type Props = {
  filterOptions?: FilterItem[];
  onChange: (keys: string[]) => void;
  title: string;
  filterState: string[];
  pendingFilters?: string[];
  noFiltersMessage?: string;
  noFiltersClassName?: string;
  startOpen?: boolean;
  children?: React.ReactNode;
  facet_counts?: FacetCountValue[];
  hideBottomBorder?: boolean;
  showSelectedFirst?: boolean;
};

const CheckboxFilterSection = ({
  filterOptions,
  onChange,
  title,
  filterState,
  pendingFilters = [],
  noFiltersMessage,
  noFiltersClassName,
  startOpen,
  facet_counts,
  children,
  hideBottomBorder = false,
  showSelectedFirst = false,
}: Props) => {
  const pathname = usePathname();

  const handleCheckboxChange = (key: string) => {
    const currentFilters =
      isMobile() && pendingFilters.length > 0 ? pendingFilters : filterState;

    const filterExists = currentFilters.some(
      (filter) =>
        typeof filter === 'string' &&
        slugify(filter.toLowerCase()) === slugify(key.toLowerCase())
    );

    const newFilters = filterExists
      ? currentFilters.filter(
          (filter) =>
            typeof filter === 'string' &&
            slugify(filter.toLowerCase()) !== slugify(key.toLowerCase())
        )
      : [...currentFilters, key];

    onChange(newFilters);
  };

  const isSelected = (optKey: string) => {
    const normalizedKey = slugify(optKey.toLowerCase());
    return filterState?.some(
      (x) => typeof x === 'string' && slugify(x.toLowerCase()) === normalizedKey
    );
  };

  function getHref(opt: string) {
    const segments = pathname?.split('/');
    if (!segments) return '/';
    const path = pathname
      ?.split('/')
      .slice(
        0,
        segments.includes('profile') ? segments.length : segments.length - 1
      );
    const lastCat = segments.includes('profile')
      ? ''
      : pathname?.split('/')[segments.length - 1];
    opt = opt.replace('%', '-percent').replace(' ', '-');
    opt = opt.replace('&', '');
    opt = opt.replace(`'`, '').replace(')', '').replace('’', '');
    let optSlug = '';
    optSlug =
      title.includes('Size') && !title.includes('Engine')
        ? 'size-' + opt
        : slugify(opt);
    const final =
      (filterState.length > 0 ? '/filters' : '') +
      path?.join('/') +
      '/' +
      optSlug +
      (lastCat ? '-' + lastCat : '');
    return final;
  }

  return (
    <div
      className={`mt-4 ${hideBottomBorder ? '' : 'border-b border-zinc-300'}`}
    >
      <Accordion
        title={title}
        titleClassName="!text-[2.1rem] sm:!text-[1.8rem] !font-semibold"
        startOpen={startOpen}
      >
        {children}
        <div className="flex max-h-[45rem] flex-col gap-3 overflow-y-auto overflow-x-hidden px-[1rem] pb-[2rem] sm:gap-4">
          {filterOptions && filterOptions.length > 0 ? (
            filterOptions
              .sort((a, b) => {
                if (showSelectedFirst) {
                  const aSelected = isSelected(a.key);
                  const bSelected = isSelected(b.key);
                  if (aSelected && !bSelected) return -1;
                  if (!aSelected && bSelected) return 1;
                }
                return 0;
              })
              .map((opt, i) => (
                <div className="flex items-center gap-x-2" key={opt.key + i}>
                  <Checkbox
                    className="rounded-md !outline-none !ring-0"
                    label={opt.label}
                    link={
                      title !== 'Ship From' &&
                      title !== 'Ship To' &&
                      !!facet_counts?.find((c) => c.value === opt.key)?.count
                        ? getHref(opt.key)
                        : undefined
                    }
                    onChange={() => handleCheckboxChange(opt.key)}
                    selected={isSelected(opt.key)}
                    key={JSON.stringify(isSelected(opt.key))}
                  />
                  {!!facet_counts?.find((c) => c.value === opt.key) && (
                    <span className="text-[1.5rem] text-brand-dark-gray sm:text-[1.2rem]">
                      ({facet_counts.find((c) => c.value === opt.key)?.count})
                    </span>
                  )}
                </div>
              ))
          ) : (
            <div className={noFiltersClassName}>
              <p className="text-center">{noFiltersMessage}</p>
            </div>
          )}
        </div>
      </Accordion>
    </div>
  );
};

export default CheckboxFilterSection;
